import { Box, Divider, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { useLiveQuery } from "@tanstack/react-db";
import { Duration } from "luxon";
import { useContext, useEffect, useMemo, useState } from "react";
import { useInterval } from "usehooks-ts";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import ScheduleCalendar from "@/components/Icons/ScheduleCalendar.tsx";
import ScheduledMessageCreateModal from "@/components/ScheduledMessageComposer/ScheduledMessageCreateModal.tsx";
import SimpleModal from "@/components/SimpleModal.tsx";
import WorkflowItem from "@/components/WorkflowItem.tsx";
import { workflowMap } from "@/components/workflowUtils";
import { DataContext } from "@/models/DataProvider";
import {
  broadcastActionsQuery,
  broadcastRecipientsListQuery,
  isUpcomingScheduleTrigger,
  scheduleTriggersQuery,
  workflowItemsQuery,
} from "@/models/repositories";
import { SchedulingContext } from "@/models/SchedulingContextProvider.tsx";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";

export default function Workflows() {
  const theme = useTheme();
  const { timezone } = useContext(SchedulingContext);
  const { loadWorkspaceWorkflowItems } = useContext(DataContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  // Advanced by the poll below, so a one-time message drops off the list once it has sent rather than sitting there
  // until the page remounts.
  const [now, setNow] = useState(new Date(Date.now()).toISOString());
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [confirmClose, setConfirmClose] = useState<boolean>(false);

  const { data: workflowItemRows } = useLiveQuery((q) => (workspaceId ? workflowItemsQuery(workspaceId)(q) : null));
  const workflowItems = workflowItemRows ?? [];

  const { data: broadcastRows } = useLiveQuery((q) => (workspaceId ? broadcastActionsQuery(workspaceId)(q) : null));
  const broadcasts = broadcastRows ?? [];

  const { data: scheduleTriggerRows } = useLiveQuery((q) =>
    workspaceId ? scheduleTriggersQuery(workspaceId)(q) : null,
  );
  const scheduleTriggers = useMemo(
    () => (scheduleTriggerRows ?? []).filter((trigger) => isUpcomingScheduleTrigger(trigger, now)),
    [scheduleTriggerRows, now],
  );

  const { data: broadcastRecipientRows } = useLiveQuery((q) =>
    workspaceId ? broadcastRecipientsListQuery(workspaceId)(q) : null,
  );
  const broadcastRecipients = broadcastRecipientRows ?? [];

  useEffect(() => {
    loadWorkspaceWorkflowItems({ workspaceId, myAccountId });
  }, [workspaceId, myAccountId]);

  const workflows = useMemo(() => {
    return workflowMap({
      timezone,
      workflowItems,
      broadcasts,
      scheduleTriggers,
      broadcastRecipients,
    });
  }, [workflowItems, broadcasts, scheduleTriggers, broadcastRecipients, timezone]);

  /**
   * Poll the server for new events when on this page, since they do not come in via app-sync
   */
  useInterval(() => {
    loadWorkspaceWorkflowItems({ workspaceId, myAccountId });
    // Only when a trigger has actually crossed the cutoff. Advancing it on every tick would re-render the whole grid
    // ten times a minute to show what it was already showing.
    setNow((previous) => {
      const next = new Date(Date.now()).toISOString();
      const crossed = (scheduleTriggerRows ?? []).some(
        (trigger) => isUpcomingScheduleTrigger(trigger, previous) !== isUpcomingScheduleTrigger(trigger, next),
      );
      return crossed ? next : previous;
    });
  }, Duration.fromObject({ seconds: 10 }).toMillis());

  return (
    <>
      <Box data-test="workflows" sx={{ p: 2, maxWidth: "auto", margin: "0 auto" }}>
        <Box
          sx={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography component="h3" sx={{ fontSize: "1.75rem", fontWeight: 700 }}>
              Scheduled Messages
            </Typography>
          </Box>
          <Box>
            <Box>
              <Button
                data-sb={TestIds.scheduled.create}
                sx={{ gap: 1 }}
                onClick={() => setModalOpen(() => true)}
                variant="contained"
              >
                <ScheduleCalendar /> <Box>New Scheduled Message</Box>
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider
          sx={{
            width: "100%",
            borderWidth: "0.75px",
            borderColor: theme.palette.secondary.light,
            mt: 3,
            mb: 2,
          }}
        />
        {workflows?.length > 0 ? (
          <>
            <Box
              sx={{
                height: `calc(100vh - 120px)`,
                pr: 1,
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  overflowY: "auto",
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(450px, 1fr))",
                  gap: 2,
                }}
              >
                {workflows?.map((item) => (
                  <WorkflowItem workflowItem={item} key={item.id} />
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center" }}>No scheduled messages</Box>
        )}
      </Box>
      <SimpleModal
        open={modalOpen}
        closeModal={() => {
          if (hasChanges) {
            setConfirmClose(() => true);
            setTimeout(() => {
              setConfirmClose(() => false);
            }, Duration.fromObject({ milliseconds: 200 }).toMillis());
          } else {
            setModalOpen(() => false);
          }
        }}
        title="Schedule a message"
        sx={{
          position: "relative",
          boxShadow: hasChanges ? `0 0 0 2px ${theme.palette.info.main}` : "",
        }}
      >
        <ScheduledMessageCreateModal
          askToConfirmClose={confirmClose}
          setHasChanges={(value: boolean) => setHasChanges(() => value)}
          closeModal={() => setModalOpen(() => false)}
        />
      </SimpleModal>
    </>
  );
}
